import { useState } from "react";
import { Copy, Loader2, RefreshCw, Clapperboard, Sun, MessageSquareQuote, Moon, Send } from "lucide-react";
import { generateStorie } from "@/lib/api";
import { toast } from "sonner";

const VIOLET = "#A855F7";

function copy(text, label) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copiata`);
}

function StoriaCard({ icon: Icon, kicker, title, children, copyText, testId, accent = VIOLET }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-[0.6rem] uppercase tracking-wider font-semibold" style={{ color: accent }}>{kicker}</span>
          <p className="text-xs font-bold text-white flex items-center gap-1.5"><Icon size={13} style={{ color: accent }} /> {title}</p>
        </div>
        <button
          data-testid={`${testId}-copy`}
          onClick={(e) => { e.stopPropagation(); copy(copyText, title); }}
          className="text-neutral-500 hover:text-white transition-colors shrink-0"
          title="Copia testo"
        >
          <Copy size={14} />
        </button>
      </div>
      <div className="text-sm text-neutral-200 leading-relaxed space-y-1.5">{children}</div>
    </div>
  );
}

export function StorieBox({ item, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const s = item.storie;

  const run = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const storie = await generateStorie(item.id);
      onUpdated({ ...item, storie });
      toast.success("Le 3 storie di oggi sono pronte");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella generazione delle storie");
    } finally {
      setLoading(false);
    }
  };

  if (!s) {
    return (
      <div className="border border-dashed rounded-sm p-4 mt-2 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: `${VIOLET}66` }} data-testid={`storie-empty-${item.id}`}>
        <div>
          <span className="label-caps flex items-center gap-2" style={{ color: VIOLET }}><Clapperboard size={13} /> Le 3 storie di oggi</span>
          <p className="text-xs text-neutral-500 mt-1">Riciclo del reel in 3 storie (mattina · pomeriggio · sera) + la storia del 20%. Zero riprese: copi e incolli.</p>
        </div>
        <button
          data-testid={`storie-generate-${item.id}`}
          onClick={run}
          disabled={loading}
          className="text-white font-bold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm uppercase tracking-wider disabled:opacity-60"
          style={{ background: VIOLET }}
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Clapperboard size={16} />}
          {loading ? "Scrivo le storie..." : "Scrivi le 3 storie"}
        </button>
      </div>
    );
  }

  const s1 = s.storia_1 || {}; const s2 = s.storia_2 || {}; const s3 = s.storia_3 || {}; const s20 = s.storia_20 || {};
  const opz = (s1.opzioni || []).filter(Boolean);
  const s1Copy = [s1.testo, opz.length ? `Sondaggio: ${opz.join(" / ")}` : (s1.domanda ? `Domanda: ${s1.domanda}` : "")].filter(Boolean).join("\n");
  const s3Copy = [s3.teaser, s3.vita_vera?.testo].filter(Boolean).join("\n");

  return (
    <div className="border rounded-sm p-4 mt-2" style={{ borderColor: `${VIOLET}66`, background: "#0f0a16" }} data-testid={`storie-box-${item.id}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <span className="label-caps flex items-center gap-2" style={{ color: VIOLET }}><Clapperboard size={13} /> Le 3 storie di oggi</span>
        <div className="flex items-center gap-3">
          {s.prossimo_tema ? (
            <span className="text-[0.65rem] text-neutral-500">Teaser dal calendario: <span className="text-neutral-300">{s.prossimo_label}</span></span>
          ) : (
            <span className="text-[0.65rem] text-neutral-600">Nessun reel in calendario per il giorno dopo: teaser generico</span>
          )}
          <button
            data-testid={`storie-regenerate-${item.id}`}
            onClick={run}
            disabled={loading}
            className="text-sm font-semibold hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-60"
            style={{ color: VIOLET }}
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            {loading ? "Rigenero..." : "Rigenera"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StoriaCard icon={Sun} kicker="Quando pubblichi · 20 sec" title="Condividi il reel" copyText={s1Copy} testId={`storia-1-${item.id}`}>
          <p>{s1.testo}</p>
          {opz.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {opz.map((o, i) => <span key={i} className="text-xs px-2 py-1 rounded-full border border-[#333] text-neutral-300">{o}</span>)}
            </div>
          )}
          {!opz.length && s1.domanda && <p className="text-xs text-neutral-400">Adesivo domanda: «{s1.domanda}»</p>}
        </StoriaCard>
        <StoriaCard icon={MessageSquareQuote} kicker="Pomeriggio · 1 min" title="Screenshot + risposta" copyText={s2.risposta || ""} testId={`storia-2-${item.id}`}>
          {s2.commento_atteso && <p className="text-xs text-neutral-500 italic">Commento che arriverà: «{s2.commento_atteso}»</p>}
          <p>{s2.risposta}</p>
        </StoriaCard>
        <StoriaCard icon={Moon} kicker="Sera · 30 sec" title="Teaser + vita vera" copyText={s3Copy} testId={`storia-3-${item.id}`}>
          {s3.teaser && <p className="font-semibold text-white">{s3.teaser}</p>}
          {s3.vita_vera?.foto && <p className="text-xs text-neutral-500">Foto: {s3.vita_vera.foto}</p>}
          {s3.vita_vera?.testo && <p>«{s3.vita_vera.testo}»</p>}
        </StoriaCard>
      </div>
      {s20.testo && (
        <div className="mt-3">
          <StoriaCard icon={Send} kicker="Storia del 20% · max 1 a settimana" title="Invito in DM «INIZIAMO»" copyText={s20.testo} testId={`storia-20-${item.id}`} accent="#22C55E">
            <p>{s20.testo}</p>
          </StoriaCard>
        </div>
      )}
    </div>
  );
}
